import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import Tooltip from '@material-ui/core/Tooltip'
import Collapse from '@material-ui/core/Collapse'
import ExpandLess from '@material-ui/icons/ExpandLess'
import ExpandMore from '@material-ui/icons/ExpandMore'
import CloseIcon from '@material-ui/icons/Close'
import DatePicker, { formatLocalYearMonthDay } from '../components/DatePicker'

import { useAuth } from '../contexts/Auth'
import { useAPIClient } from 'react-toolbox/APIClient'
import { useMutation } from 'react-query'
import {
    List,
    ListItem,
    ListItemText,
    Table,
    TableBody,
    TableCell,
    TableRow
} from '@material-ui/core'

export default function IntegrationServerPage() {
    // Function to get bearer token
    const { getFzAuthSvcToken } = useAuth()

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Typography variant="h3">Integration Server</Typography>
            </Grid>
            <Grid item xs={12}>
                <ViewInventoryCard getToken={getFzAuthSvcToken} />
            </Grid>
            <Grid item xs={12}>
                <ViewLeadsCard getToken={getFzAuthSvcToken} />
            </Grid>
            <Grid item xs={12}>
                <ViewLinksCard getToken={getFzAuthSvcToken} />
            </Grid>
        </Grid>
    )
}

// Collect string values from a JSON object (keys are ignored)
const collectJsonValues = (value, out = []) => {
    if (value === null || value === undefined) {
        return out
    }
    if (Array.isArray(value)) {
        value.forEach((item) => collectJsonValues(item, out))
        return out
    }
    if (typeof value === 'object') {
        Object.values(value).forEach((item) => collectJsonValues(item, out))
        return out
    }
    out.push(String(value))
    return out
}

const filterByJsonValues = (items, search) => {
    const q = search.trim().toLowerCase()
    // Require at least 2 characters before filtering
    if (q.length < 2) return items
    return items.filter((item) => {
        const values = collectJsonValues(item).join(' ').toLowerCase()
        return values.includes(q)
    })
}

function SearchResults({ items, cid, kind }) {
    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const isVehicles = kind === 'vehicles'

    // Debounce filtering so large lists aren't re-scanned on every keystroke
    useEffect(() => {
        const id = setTimeout(() => setDebouncedSearch(search), 250)
        return () => clearTimeout(id)
    }, [search])

    const filtered = filterByJsonValues(items, debouncedSearch)
    // Collapse expanded cards only when the visible set changes, not every keystroke
    const collapseKey = filtered
        .map((el) =>
            isVehicles ? el.FrazerID : el.Partner + '-' + el.PartnerID
        )
        .join(',')

    const clearSearch = () => {
        setSearch('')
        setDebouncedSearch('')
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                    }}
                >
                    <TextField
                        label={isVehicles ? 'Search vehicles' : 'Search leads'}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        fullWidth
                        placeholder="Type at least 2 characters..."
                    />
                    <Tooltip title="Clear">
                        <span style={{ marginTop: 8 }}>
                            <Button
                                variant="contained"
                                size="small"
                                type="button"
                                onClick={clearSearch}
                                disabled={search === ''}
                                aria-label="Clear"
                                style={{
                                    minWidth: 32,
                                    width: 32,
                                    height: 32,
                                    padding: 0
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </Button>
                        </span>
                    </Tooltip>
                </div>
            </Grid>
            <Grid item xs={12}>
                <List dense={true}>
                    <ListItem>
                        <ListItemText
                            primary={
                                'Showing ' +
                                filtered.length +
                                ' of ' +
                                items.length +
                                (isVehicles ? ' vehicle(s)' : ' lead(s)') +
                                ' for CID ' +
                                cid
                            }
                        />
                    </ListItem>
                    {filtered.map((el) =>
                        isVehicles ? (
                            <VehicleCard
                                key={el.FrazerID}
                                vehicle={el}
                                collapseKey={collapseKey}
                            />
                        ) : (
                            <LeadCard
                                key={el.Partner + '-' + el.PartnerID}
                                lead={el}
                                collapseKey={collapseKey}
                            />
                        )
                    )}
                </List>
            </Grid>
        </Grid>
    )
}
SearchResults.propTypes = {
    items: PropTypes.array.isRequired,
    cid: PropTypes.string.isRequired,
    kind: PropTypes.oneOf(['vehicles', 'leads']).isRequired
}

function ViewInventoryCard({ getToken }) {
    // State
    const [cid, setCID] = useState('')
    const [result, setResult] = useState('')

    // API interactions
    const api = useAPIClient()
    const { mutateAsync: viewInventory, isLoading: viewInventoryIsLoading } =
        useMutation(api.integrationServer.viewInventory, {
            onSuccess: (response) => {
                const items = Array.isArray(response) ? response : []
                setResult(
                    <SearchResults items={items} cid={cid} kind="vehicles" />
                )
            },
            onError: (err) => {
                // If we can't reach the integration server, we expect an error
                // object with a message field. If we can reach the integration
                // server, we expect an error string.
                setResult(
                    err.error?.message ||
                        err.error ||
                        `An unexpected error occurred: ${JSON.stringify(err)}`
                )
            }
        })

    // Form submit handler
    const handleSubmit = async (e) => {
        e.preventDefault()
        const token = await getToken()
        viewInventory({
            token,
            cid
        })
    }

    return (
        <Card>
            <CardContent>
                <form onSubmit={handleSubmit}>
                    <fieldset disabled={viewInventoryIsLoading}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="h6">
                                    View Inventory
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    required
                                    label="CID"
                                    value={cid}
                                    onChange={(e) => {
                                        setCID(e.target.value)
                                    }}
                                    fullWidth
                                    inputProps={{
                                        inputMode: 'numeric',
                                        pattern: '[0-9]*'
                                    }}
                                />
                            </Grid>
                            <Grid item>
                                <Button
                                    variant="contained"
                                    size="large"
                                    type="submit"
                                >
                                    Submit
                                </Button>
                            </Grid>
                            {result && (
                                <Grid item xs={12}>
                                    {result}
                                </Grid>
                            )}
                        </Grid>
                    </fieldset>
                </form>
            </CardContent>
        </Card>
    )
}
ViewInventoryCard.propTypes = {
    getToken: PropTypes.func.isRequired
}

function CleanupForm({ getToken, cid, onDeleted }) {
    const confirmPrompt = 'I understand'
    const [cleanUpTo, setCleanUpTo] = useState(null)
    const [confirm, setConfirm] = useState('')
    const [preview, setPreview] = useState(null)
    const [message, setMessage] = useState('')

    const cleanUpToValue = formatLocalYearMonthDay(cleanUpTo)
    const previewMatches =
        preview !== null &&
        preview.cid === cid &&
        preview.cleanUpTo === cleanUpToValue

    const api = useAPIClient()
    const { mutateAsync: previewCleanup, isLoading: previewIsLoading } =
        useMutation(api.integrationServer.previewCleanup, {
            onSuccess: (response, variables) => {
                setPreview({
                    cid: variables.cid,
                    cleanUpTo: variables.cleanUpTo || '',
                    count: response.count
                })
                setMessage('')
            },
            onError: (err) => {
                setPreview(null)
                setMessage(
                    err.error?.message ||
                        err.error ||
                        `An unexpected error occurred: ${JSON.stringify(err)}`
                )
            }
        })
    const { mutateAsync: deleteRecords, isLoading: deleteIsLoading } =
        useMutation(api.integrationServer.deleteRecords, {
            onSuccess: (response) => {
                setPreview(null)
                setConfirm('')
                setMessage(`Cleaned up ${response.count} lead(s).`)
                onDeleted()
            },
            onError: (err) => {
                setMessage(
                    err.error?.message ||
                        err.error ||
                        `An unexpected error occurred: ${JSON.stringify(err)}`
                )
            }
        })

    const clearOutcome = () => {
        setPreview(null)
        setMessage('')
    }

    const handlePreview = async () => {
        const token = await getToken()
        previewCleanup({
            token,
            cid,
            cleanUpTo: cleanUpToValue
        })
    }

    const handleDelete = async (e) => {
        e.preventDefault()
        if (!previewMatches || confirm !== confirmPrompt) {
            return
        }
        const token = await getToken()
        deleteRecords({
            token,
            cid,
            cleanUpTo: cleanUpToValue
        })
    }

    return (
        <form
            aria-label="Clean up leads"
            onSubmit={handleDelete}
            style={{ marginTop: 16 }}
        >
            <fieldset disabled={previewIsLoading || deleteIsLoading}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="h6">Clean up leads</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2">
                            Clears leads that can no longer be read after an
                            auth key change. A date deletes leads created on or
                            before that day. Leave it blank to clean up all
                            leads.
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <DatePicker
                            label="Clean up through"
                            helperText="Optional. Leads created on or before this date, or all leads if omitted."
                            selected={cleanUpTo}
                            dateFormat="yyyy-MM-dd"
                            clearable
                            fullWidth
                            onChange={(date) => {
                                setCleanUpTo(date)
                                clearOutcome()
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            id="cleanup-confirm"
                            label={`To delete, type "${confirmPrompt}".`}
                            value={confirm}
                            onChange={(e) => {
                                setConfirm(e.target.value)
                            }}
                            fullWidth
                        />
                    </Grid>
                    <Grid item>
                        <Button
                            variant="contained"
                            size="large"
                            type="button"
                            onClick={handlePreview}
                        >
                            Preview
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button
                            variant="contained"
                            size="large"
                            type="submit"
                            disabled={
                                !previewMatches || confirm !== confirmPrompt
                            }
                        >
                            Delete
                        </Button>
                    </Grid>
                    {previewMatches && (
                        <Grid item xs={12}>
                            <Typography>
                                Would clean up {preview.count} lead(s).
                            </Typography>
                        </Grid>
                    )}
                    {message && (
                        <Grid item xs={12}>
                            <Typography>{message}</Typography>
                        </Grid>
                    )}
                </Grid>
            </fieldset>
        </form>
    )
}
CleanupForm.propTypes = {
    getToken: PropTypes.func.isRequired,
    cid: PropTypes.string.isRequired,
    onDeleted: PropTypes.func.isRequired
}

// The data view is complicated enough to merit being broken out (it has state per displayed vehicle)
function VehicleCard({ vehicle, collapseKey }) {
    const [open, setOpen] = useState(false)

    // Collapse when the filtered result set changes
    useEffect(() => {
        setOpen(false)
    }, [collapseKey])

    const handleClick = () => {
        setOpen(!open)
    }

    return (
        <>
            <ListItem button onClick={handleClick}>
                <ListItemText
                    primary={
                        vehicle.Data.Stock +
                        ' - ' +
                        vehicle.Data.Year +
                        ' ' +
                        vehicle.Data.Make +
                        ' ' +
                        vehicle.Data.Model +
                        ' (VehicleSysID: ' +
                        vehicle.FrazerID +
                        ')'
                    }
                />
                {open ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse in={open}>
                <Table size="small">
                    <TableBody>
                        {Object.keys(vehicle.Data).map((k) => (
                            <TableRow key={k}>
                                <TableCell>{k}</TableCell>
                                <TableCell>
                                    {JSON.stringify(vehicle.Data[k])}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Collapse>
        </>
    )
}
VehicleCard.propTypes = {
    vehicle: PropTypes.shape({
        FrazerID: PropTypes.number.isRequired,
        Data: PropTypes.shape({
            Stock: PropTypes.string.isRequired,
            Year: PropTypes.number.isRequired,
            Make: PropTypes.string.isRequired,
            Model: PropTypes.string.isRequired
        }).isRequired
    }).isRequired,
    collapseKey: PropTypes.string
}

function ViewLeadsCard({ getToken }) {
    // State
    const [cid, setCID] = useState('')
    const [result, setResult] = useState('')

    // API interactions
    const api = useAPIClient()
    const { mutateAsync: viewLeads, isLoading: viewLeadsIsLoading } =
        useMutation(api.integrationServer.viewLeads, {
            onSuccess: (response) => {
                const items = Array.isArray(response) ? response : []
                setResult(
                    <SearchResults items={items} cid={cid} kind="leads" />
                )
            },
            onError: (err) => {
                // If we can't reach the integration server, we expect an error
                // object with a message field. If we can reach the integration
                // server, we expect an error string.
                setResult(
                    err.error?.message ||
                        err.error ||
                        `An unexpected error occurred: ${JSON.stringify(err)}`
                )
            }
        })

    // Form submit handler
    const handleSubmit = async (e) => {
        e.preventDefault()
        const token = await getToken()
        viewLeads({
            token,
            cid
        })
    }

    const refreshLeads = async () => {
        const token = await getToken()
        viewLeads({
            token,
            cid
        })
    }

    // A string is the initial empty value or the error text from onError.
    // A successful list is the SearchResults element.
    const leadsListed = Boolean(result) && typeof result !== 'string'

    return (
        <Card>
            <CardContent>
                <form onSubmit={handleSubmit}>
                    <fieldset disabled={viewLeadsIsLoading}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="h6">View Leads</Typography>
                            </Grid>
                            {!leadsListed && (
                                <Grid item xs={12}>
                                    <Typography variant="body2">
                                        View leads for a customer to get the
                                        clean up UI.
                                    </Typography>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <TextField
                                    required
                                    label="CID"
                                    value={cid}
                                    onChange={(e) => {
                                        setCID(e.target.value)
                                        setResult('')
                                    }}
                                    fullWidth
                                    inputProps={{
                                        inputMode: 'numeric',
                                        pattern: '[0-9]*'
                                    }}
                                />
                            </Grid>
                            <Grid item>
                                <Button
                                    variant="contained"
                                    size="large"
                                    type="submit"
                                >
                                    Submit
                                </Button>
                            </Grid>
                            {result && (
                                <Grid item xs={12}>
                                    {result}
                                </Grid>
                            )}
                        </Grid>
                    </fieldset>
                </form>
                {leadsListed && (
                    <CleanupForm
                        getToken={getToken}
                        cid={cid}
                        onDeleted={refreshLeads}
                    />
                )}
            </CardContent>
        </Card>
    )
}
ViewLeadsCard.propTypes = {
    getToken: PropTypes.func.isRequired
}

// The data view is complicated enough to merit being broken out (it has state per displayed lead)
function LeadCard({ lead, collapseKey }) {
    const [open, setOpen] = useState(false)

    // Collapse when the filtered result set changes.
    useEffect(() => {
        setOpen(false)
    }, [collapseKey])

    const handleClick = () => {
        setOpen(!open)
    }

    return (
        <>
            <ListItem button onClick={handleClick}>
                <ListItemText primary={lead.Partner + ' ' + lead.PartnerID} />
                {open ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse in={open}>
                <Table size="small">
                    <TableBody>
                        {Object.keys(lead.Summary).map((k) => (
                            <TableRow key={k}>
                                <TableCell>{k}</TableCell>
                                <TableCell>
                                    {JSON.stringify(lead.Summary[k])}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Collapse>
        </>
    )
}
LeadCard.propTypes = {
    lead: PropTypes.shape({
        Partner: PropTypes.string.isRequired,
        PartnerID: PropTypes.string.isRequired,
        Summary: PropTypes.shape({
            Buyer: PropTypes.shape({
                LastName: PropTypes.string.isRequired
            }).isRequired
        }).isRequired
    }).isRequired,
    collapseKey: PropTypes.string
}

function ViewLinksCard({ getToken }) {
    // State
    const [input, setInput] = useState('')
    const [result, setResult] = useState('')

    // API interactions
    const api = useAPIClient()
    const { mutateAsync: viewLinks, isLoading: viewLinksIsLoading } =
        useMutation(api.integrationServer.viewLinks, {
            onSuccess: (response) => {
                setResult(
                    <List dense={true}>
                        {response.Dealers && (
                            <ListItem key="LinkedDealersTotal">
                                <ListItemText
                                    primary={
                                        'There are ' +
                                        response.Dealers.length +
                                        ' linked dealer(s)'
                                    }
                                />
                            </ListItem>
                        )}
                        {response.Dealers &&
                            response.Dealers.map((el) => {
                                return (
                                    <ListItem key={el}>
                                        <ListItemText primary={el} />
                                    </ListItem>
                                )
                            })}
                        {response.Partners && (
                            <ListItem key="LinkedPartnersTotal">
                                <ListItemText
                                    primary={
                                        'There are ' +
                                        response.Partners.length +
                                        ' linked partner(s)'
                                    }
                                />
                            </ListItem>
                        )}
                        {response.Partners &&
                            response.Partners.map((el) => {
                                return (
                                    <ListItem key={el}>
                                        <ListItemText primary={el} />
                                    </ListItem>
                                )
                            })}
                    </List>
                )
            },
            onError: (err) => {
                // If we can't reach the integration server, we expect an error
                // object with a message field. If we can reach the integration
                // server, we expect an error string.
                setResult(
                    err.error?.message ||
                        err.error ||
                        `An unexpected error occurred: ${JSON.stringify(err)}`
                )
            }
        })

    // Form submit handler
    const handleSubmit = async (e) => {
        e.preventDefault()
        const token = await getToken()

        let cid
        let code

        // Input is a CID if it's an integer.
        if (Number.isNaN(Number.parseInt(input))) {
            code = input
        } else {
            cid = input
        }

        viewLinks({
            token,
            cid,
            code
        })
    }

    return (
        <Card>
            <CardContent>
                <form onSubmit={handleSubmit}>
                    <fieldset disabled={viewLinksIsLoading}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="h6">
                                    View Partner/Dealer Links
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="CID or Partner Code"
                                    value={input}
                                    onChange={(e) => {
                                        setInput(e.target.value)
                                    }}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item>
                                <Button
                                    variant="contained"
                                    size="large"
                                    type="submit"
                                >
                                    Submit
                                </Button>
                            </Grid>
                            {result && (
                                <Grid item xs={12}>
                                    {result}
                                </Grid>
                            )}
                        </Grid>
                    </fieldset>
                </form>
            </CardContent>
        </Card>
    )
}
ViewLinksCard.propTypes = {
    getToken: PropTypes.func.isRequired
}
